import React, { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Form, Input, Button, message, Table, Col, Row, Tooltip } from 'antd';
import { DownSquareOutlined, UpSquareOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx'; // Import xlsx library
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface Employee {
    employeeName: string;
    visitCount: number;
    officeLocation: string;
    city: string;
}

interface FootFallReport {
    country: string;
    employees: Employee[];
    totalCount: number;
}

const FootFall: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [footFallReportData, setfootFallReportData] = useState<FootFallReport[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const chartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchToken();
    }, []);

  const fetchToken = async () => {
          const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
          });
        return res.idToken;
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
    };
    
    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
    };

    const handleSubmit = async () => {
        if (!fromDate || !toDate) {
            message.error('Please fill all fields');
            return;
        }
        if (new Date(toDate) < new Date(fromDate)) {
            message.error('To-Date must be greater than From-Date');
            return;
        }
        const API_URL = process.env.REACT_APP_API_URL;

        try 
        {
            const token = await fetchToken();
            const response = await axios.get(`${API_URL}/api/FootFallReport/GetFootFallData`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: {
                    fromDate,
                    toDate,
                },
            });
            setfootFallReportData(response.data);
            setShowReport(true);
            console.log('Report Data:', response.data);
        } catch (error) 
        {
            message.error(`Failed to get report data: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleDownloadExcel = () => {
        if (footFallReportData.length === 0) {
            message.error('No data available to download');
            return;
        }

        // Flatten data to include employee details
        const formattedData: any[] = [];
        footFallReportData.forEach((countryData) => {
            countryData.employees.forEach((employee) => {
                formattedData.push({
                    Country: countryData.country,
                    EmployeeName: employee.employeeName,
                    OfficeLocation: employee.officeLocation,
                    City: employee.city,
                    VisitCount: employee.visitCount,
                });
            });
        });

        // Convert data to worksheet
        const worksheet = XLSX.utils.json_to_sheet(formattedData);

        // Create a new workbook and append the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'FootFallReport');

        // Generate today's date in YYYY-MM-DD format
        const today = new Date();
        const formattedDate = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD

        // Generate Excel file and trigger download
        XLSX.writeFile(workbook, `FootFallReport_${formattedDate}.xlsx`);
    };

    const handleDownloadPDF = async () => {
        if (footFallReportData.length === 0) {
            message.error('No data available to download');
            return;
        }

        if (!chartRef.current) {
            message.error('Chart not found');
            return;
        }

        try {
            // Capture the chart as canvas
            const canvas = await html2canvas(chartRef.current, {
                scale: 2, // Higher quality
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/png');
            
            // Create PDF
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            });

            // Calculate dimensions to fit the page
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pdfWidth - 20; // 10mm margin on each side
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Add title
            pdf.setFontSize(16);
            pdf.text('Foot Fall Report by Country', pdfWidth / 2, 15, { align: 'center' });

            // Add date range
            pdf.setFontSize(10);
            pdf.text(`Period: ${fromDate} to ${toDate}`, pdfWidth / 2, 22, { align: 'center' });

            // Add chart image
            const yPosition = 30;
            pdf.addImage(imgData, 'PNG', 10, yPosition, imgWidth, Math.min(imgHeight, pdfHeight - yPosition - 10));

            // Generate today's date for filename
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];

            // Save PDF
            pdf.save(`FootFallReport_Chart_${formattedDate}.pdf`);
            message.success('PDF downloaded successfully');
        } catch (error) {
            message.error(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleExpandAll = () => {
        const allKeys = footFallReportData.map(item => item.country);
        setExpandedRowKeys(allKeys);
    };

    const handleCollapseAll = () => {
        setExpandedRowKeys([]);
    };

    const columns = [
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>Country</span>
                    <Tooltip title="Expand All">
                        <DownSquareOutlined 
                            onClick={handleExpandAll}
                            style={{ fontSize: '16px', cursor: 'pointer', color: '#1890ff' }}
                        />
                    </Tooltip>
                    <Tooltip title="Collapse All">
                        <UpSquareOutlined 
                            onClick={handleCollapseAll}
                            style={{ fontSize: '16px', cursor: 'pointer', color: '#1890ff' }}
                        />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'country',
            key: 'country',
            sorter: (a: FootFallReport, b: FootFallReport) => a.country.localeCompare(b.country),
        },
        {
            title: 'Number of Employees',
            key: 'employeeCount',
            render: (record: FootFallReport) => record.employees.length,
        },
    ];

    const expandedRowRender = (record: FootFallReport) => {
        const employeeColumns = [
            {
                title: 'Employee Name',
                dataIndex: 'employeeName',
                key: 'employeeName',
            },
            {
                title: 'Office Location',
                dataIndex: 'officeLocation',
                key: 'officeLocation',
            },
            {
                title: 'City',
                dataIndex: 'city',
                key: 'city',
            },
            {
                title: 'Visit Count',
                dataIndex: 'visitCount',
                key: 'visitCount',
                sorter: (a: Employee, b: Employee) => a.visitCount - b.visitCount,
            },
        ];

        return (
            <Table
                columns={employeeColumns}
                dataSource={record.employees}
                pagination={false}
                rowKey="employeeName"
                size="small"
            />
        );
    };

   return (
    <div>
        <div>
            <Form layout="vertical" onFinish={handleSubmit}>
                <Row gutter={16} align="bottom">
                    <Col span={6}>
                        <Form.Item label="From Date" required>
                            <Input
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label="To Date" required>
                            <Input
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Get Report
                            </Button>
                            <Button type="default" onClick={handleDownloadExcel} style={{ marginLeft: '16px' }}>
                                Download as Excel
                            </Button>
                            <Button type="default" onClick={handleDownloadPDF} style={{ marginLeft: '16px' }}>
                                Download Chart as PDF
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </div>
        {showReport && (
            <div>
                {footFallReportData.length > 0 && (
                    <div ref={chartRef} style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#ffffff' }}>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={footFallReportData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="country" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={100}
                                />
                                <YAxis label={{ value: 'Total Count', angle: -90, position: 'insideLeft' }} />
                                <RechartsTooltip />
                                <Legend />
                                <Bar dataKey="totalCount" fill="#1890ff" name="Total Count" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
                
                <Table
                    dataSource={footFallReportData}
                    columns={columns}
                    rowKey="country"
                    pagination={{ pageSize: 50 }}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => record.employees.length > 0,
                        expandedRowKeys: expandedRowKeys,
                        onExpandedRowsChange: (expandedKeys) => setExpandedRowKeys(expandedKeys as string[]),
                    }}
                />
            </div>
        )}
    </div>
  );
};

export default FootFall;